import API from "./api";
import { getComponents } from "../Moduels/Salary/SalaryApi";
import { fetchWfSetupStatus } from "../Moduels/attendance/wfApi";
import { fetchLeaveTypes } from "../Moduels/leave/leaveApi";
import { loadOrganisationMe, loadOrgProfileStatus, loadSetupStatus } from "./appQueries";

/** @typedef {{ id: string, label: string, complete: boolean, required: boolean, path: string, group: string, description?: string }} SetupItem */

function hasOrgProfile(org = {}) {
  const name = String(org.name || org.legal_name || "").trim();
  const pan = String(org.pan || "").trim();
  const tan = String(org.tan_number || org.tax_id || "").trim();
  const state = String(org.state || "").trim();
  const address = String(org.address || "").trim();
  return Boolean(name && pan && tan && state && address);
}

/**
 * Payroll Settings is complete only after an explicit save from the Guided Setup
 * Wizard (or Payroll Settings save) — not from Organisation Profile foundation
 * defaults, merge_payroll_cfg defaults, or Attendance working-days alone.
 */
function hasExplicitPayrollSettingsSave(payroll = {}) {
  if (payroll.settings_completed === true) return true;
  if (payroll.settings_saved_at) return true;
  return false;
}

/**
 * Salary Components is complete only after the user confirms them in the Guided
 * Setup Wizard / Hub — not when seed-defaults auto-creates BASIC/HRA/etc.
 */
function hasExplicitSalaryComponentsSave(payroll = {}) {
  if (payroll.components_completed === true) return true;
  if (payroll.components_saved_at) return true;
  return false;
}

function hasPayrollSettings(hr = {}, payroll = {}) {
  if (!hasExplicitPayrollSettingsSave(payroll)) return false;
  const cycle = hr.salary_cycle || payroll.salary_cycle;
  const processingDay = hr.payroll_processing_day ?? payroll.payroll_processing_day;
  const divisor =
    hr.monthly_salary_divisor_days ?? payroll.monthly_salary_divisor_days;
  return Boolean(
    cycle &&
      processingDay != null &&
      divisor != null &&
      Number(divisor) > 0
  );
}

function hasNonEmpty(value) {
  return Boolean(String(value || "").trim());
}

function hasStatutoryFromModule(statutoryModule) {
  if (!statutoryModule) return false;
  const { epf, esi, pt, lwf, bonus, registrations } = statutoryModule;
  if (epf?.is_enabled) return true;
  if (esi?.is_enabled) return true;
  if (pt?.is_enabled) return true;
  if (lwf?.is_enabled) return true;
  if (bonus?.is_enabled) return true;
  // Registrations saved in Statutory wizard count even if modules are not toggled on.
  if (
    hasNonEmpty(registrations?.epfo_establishment_code) ||
    hasNonEmpty(registrations?.esic_registration_number) ||
    hasNonEmpty(registrations?.pt_registration_number)
  ) {
    return true;
  }
  return false;
}

function hasExplicitStatutorySetup(payroll = {}) {
  if (payroll.statutory_completed === true) return true;
  if (payroll.statutory_saved_at) return true;
  return false;
}

function hasStatutoryBasics(org = {}, statutoryModule = null, setupStatus = null, payroll = {}) {
  if (hasExplicitStatutorySetup(payroll)) return true;
  if (hasStatutoryFromModule(statutoryModule)) return true;
  if (setupStatus?.statutory_configured) return true;
  // Statutory-only org fields — TAN from company profile is not statutory setup.
  return Boolean(
    hasNonEmpty(org.epfo_code) ||
      hasNonEmpty(org.esic_code) ||
      hasNonEmpty(org.professional_tax_number)
  );
}

function hasExplicitLeaveSetup(payroll = {}) {
  if (payroll.leave_completed === true) return true;
  if (payroll.leave_saved_at) return true;
  return false;
}

/**
 * Leave is configured after an explicit finish, or once at least one leave type exists.
 * (The leave *settings* row is not a signal: GET /leave/settings creates a default row
 * on first read, so it exists even when nothing was configured.)
 */
export function hasLeaveModuleConfigured(leaveTypes, payroll = {}) {
  if (hasExplicitLeaveSetup(payroll)) return true;
  return Array.isArray(leaveTypes) && leaveTypes.length > 0;
}

function isAttendanceConfigured(wfStatus, setupStatus) {
  // Prefer an explicit completion timestamp from Attendance Configuration.
  if (wfStatus?.setup_completed_at) return true;
  if (wfStatus?.profile?.setup_completed_at || wfStatus?.attendance_profile?.setup_completed_at) {
    return true;
  }
  const stage = String(
    wfStatus?.setup_stage || wfStatus?.profile?.setup_stage || ""
  ).toUpperCase();
  // Only treat fully active/complete stages as done — CONFIG_REQUIRED means unfinished.
  if (["ACTIVE", "COMPLETE"].includes(stage)) {
    const modes = wfStatus?.enabled_modes || wfStatus?.setup_progress?.step_1?.sources || [];
    if (Array.isArray(modes) && modes.length > 0) return true;
    if (wfStatus?.default_source || wfStatus?.profile?.default_source) return true;
  }
  const org = setupStatus?.org || {};
  const ad = org.attendance_defaults || org.attendance || {};
  const method = ad.attendance_method;
  // Ignore placeholder/default hybrid — require a concrete capture method the user chose.
  if (method && String(method).trim() && !["hybrid", "not_set", "none"].includes(String(method).toLowerCase())) {
    return true;
  }
  if (wfStatus?.profile?.capture_mode || wfStatus?.attendance_profile?.capture_mode) {
    return true;
  }
  return false;
}

function hasSalaryComponentsConfigured(payroll = {}, componentCount = 0, payrollReadiness = null) {
  const count = Number(componentCount) || Number(payrollReadiness?.component_count) || 0;
  if (count <= 0) return false;
  if (hasExplicitSalaryComponentsSave(payroll)) return true;
  // Migration: structures already assigned means components were intentionally used.
  const assignDone = payrollReadiness?.steps?.find((s) => s.key === "assign_structure")?.done;
  return Boolean(assignDone);
}

function uniqueById(items) {
  const seen = new Set();
  return items.filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function stepStatusLabel(complete, isCurrent) {
  if (complete) return "Completed";
  if (isCurrent) return "In progress";
  return "Not started";
}

/**
 * Aggregate organisation setup state for dashboard + payroll gates.
 */
export async function fetchSetupProgress() {
  const out = {
    loading: false,
    items: /** @type {SetupItem[]} */ ([]),
    percent: 0,
    payrollReady: false,
    orgProfileComplete: false,
    employeeCount: 0,
    salaryComponentCount: 0,
    attendanceEnabled: true,
    attendanceConfigured: false,
    holidayCalendarReady: false,
    alerts: /** @type {{ id: string, level: 'warn'|'block', message: string, path: string }[]} */ ([]),
  };

  let setupStatus = null;
  let hrSettings = null;
  let wfStatus = null;
  let profileStatus = null;
  let statutoryModule = null;
  let employees = [];
  let components = [];

  let payrollReadiness = null;
  let leaveTypes = null;

  try {
    const [setupRes, hrRes, empRes, compRes, wfRes, profileRes, statutoryRes, payrollReadyRes, leaveTypesRes] = await Promise.allSettled([
      loadSetupStatus(),
      API.get("/organisation/me/hr-settings"),
      API.get("/employees/", { params: { page: 1, page_size: 1, active_only: false } }),
      getComponents(),
      fetchWfSetupStatus(),
      loadOrgProfileStatus(),
      API.get("/statutory/module"),
      API.get("/setup/payroll-readiness"),
      loadOrganisationMe()
        .then((org) => {
          const orgId = org?.id || org?.organisation_id;
          // Read-only list (never seeds); empty until an admin creates a leave type.
          return orgId ? fetchLeaveTypes(orgId).catch(() => null) : null;
        })
        .catch(() => null),
    ]);

    if (setupRes.status === "fulfilled") setupStatus = setupRes.value;
    if (hrRes.status === "fulfilled") hrSettings = hrRes.value?.data;
    if (empRes.status === "fulfilled") {
      const d = empRes.value?.data;
      employees = Array.isArray(d) ? d : d?.items || d?.employees || [];
      out.employeeCount = Number(d?.total ?? d?.count ?? employees.length) || 0;
    }
    if (compRes.status === "fulfilled") {
      components = Array.isArray(compRes.value) ? compRes.value : compRes.value?.items || [];
      out.salaryComponentCount = components.length;
    }
    if (wfRes.status === "fulfilled") wfStatus = wfRes.value;
    if (profileRes.status === "fulfilled") profileStatus = profileRes.value;
    if (statutoryRes.status === "fulfilled") statutoryModule = statutoryRes.value?.data;
    if (payrollReadyRes.status === "fulfilled") payrollReadiness = payrollReadyRes.value?.data;
    if (leaveTypesRes.status === "fulfilled") {
      const v = leaveTypesRes.value;
      leaveTypes = Array.isArray(v) ? v : v?.items || null;
    }
  } catch {
    /* partial data ok */
  }

  const hr = hrSettings?.hr_settings || {};
  // Use stored payroll subsection only — never merge_payroll_cfg /setup/status defaults
  // (those inject salary_cycle=monthly / processing_day=25 and falsely mark settings done).
  const payrollStored = { ...(hr.payroll || {}) };
  const org = {
    ...(setupStatus?.org || {}),
    ...hr,
    payroll: payrollStored,
    ...(profileStatus?.org || {}),
  };
  out.orgProfileComplete = profileStatus?.profile_complete ?? hasOrgProfile(org);
  if (profileStatus?.has_organisation === false) {
    out.alerts.push({
      id: "no_organisation",
      level: "block",
      message: "Your account is not linked to an organisation.",
      path: "/dashboard",
    });
  }
  const orgPageEmpty = profileStatus
    ? profileStatus.profile_empty === true ||
      (profileStatus.profile_empty == null &&
        !["name", "legal_name", "pan", "tan_number", "state", "address"].some((key) =>
          String(profileStatus.org?.[key] || "").trim()
        ))
    : !out.orgProfileComplete;
  if (profileStatus?.has_organisation !== false && orgPageEmpty) {
    out.alerts.push({
      id: "org_profile_incomplete",
      level: "warn",
      message: "Organisation Profile is empty — add your company name, state, and address.",
      path: "/onboarding/org-profile",
    });
  }
  const payrollSettingsDone = hasPayrollSettings(hr, payrollStored);
  const deptOk = (setupStatus?.departments_count || 0) > 0;
  const desigOk = (setupStatus?.designations_count || 0) > 0;
  const locOk = (setupStatus?.locations_count || 0) > 0;
  const orgStructureOk = deptOk && desigOk && locOk;
  const orgStepComplete = out.orgProfileComplete && orgStructureOk;
  if (!out.employeeCount && Number(payrollReadiness?.employee_count) > 0) {
    out.employeeCount = Number(payrollReadiness.employee_count);
  }
  const employeesOk = out.employeeCount > 0;
  if (!out.salaryComponentCount && Number(payrollReadiness?.component_count) > 0) {
    out.salaryComponentCount = Number(payrollReadiness.component_count);
  }
  const salaryComponentsOk = hasSalaryComponentsConfigured(
    payrollStored,
    out.salaryComponentCount,
    payrollReadiness
  );
  const payrollPipelineComplete = payrollReadiness?.complete === true;
  const statutoryOk = hasStatutoryBasics(org, statutoryModule, setupStatus, payrollStored);

  const attComplete = isAttendanceConfigured(wfStatus, setupStatus);
  out.attendanceConfigured = attComplete;
  const leaveConfigured = hasLeaveModuleConfigured(leaveTypes, payrollStored);
  out.holidayCalendarReady = Boolean(
    wfStatus?.holiday_calendar_ready ||
      wfStatus?.tasks?.some?.((t) => t.code === "holiday_calendar" && t.status === "complete")
  );

  // Deep-link org step to the first incomplete piece of company setup.
  let orgStepPath = "/settings/organization/company-profile";
  if (orgPageEmpty) {
    orgStepPath = "/onboarding/org-profile";
  } else if (!deptOk) {
    orgStepPath = "/settings/organization/structure?tab=departments";
  } else if (!desigOk) {
    orgStepPath = "/settings/organization/structure?tab=designations";
  } else if (!locOk) {
    orgStepPath = "/settings/organization/structure?tab=branches";
  }

  const missingOrgBits = [];
  if (!out.orgProfileComplete) missingOrgBits.push("company profile");
  if (!deptOk) missingOrgBits.push("departments");
  if (!desigOk) missingOrgBits.push("designations");
  if (!locOk) missingOrgBits.push("locations");

  /** @type {SetupItem[]} — meaningful one-by-one order for the dashboard */
  const rawItems = [
    {
      id: "org_profile",
      label: "Organization Profile",
      complete: orgStepComplete,
      required: true,
      path: orgStepPath,
      group: "Organization",
      description: missingOrgBits.length
        ? `Finish: ${missingOrgBits.join(", ")}`
        : "Company profile, departments, designations, and locations",
      step: 1,
    },
    {
      id: "employees",
      label: "Add Employees",
      complete: employeesOk,
      required: true,
      path: "/employees/add",
      group: "Employees",
      description: "Add at least one employee to the organisation",
      step: 2,
    },
    {
      id: "payroll_settings",
      label: "Payroll Settings",
      complete: payrollSettingsDone,
      required: true,
      path: "/salary-v2/setup",
      group: "Payroll",
      description: "Pay cycle, processing day, and working-days divisor",
      step: 3,
    },
    {
      id: "attendance_config",
      label: "Attendance Configuration",
      complete: attComplete,
      required: true,
      path: "/attendance/configuration",
      group: "Attendance",
      description: "Capture method and working days for salary",
      step: 4,
    },
    {
      id: "salary_components",
      label: "Salary Components",
      complete: salaryComponentsOk,
      required: true,
      path: "/salary-v2/components",
      group: "Payroll",
      description: "Earnings and deductions for payroll",
      step: 5,
    },
    {
      id: "statutory",
      label: "Statutory Setup",
      complete: statutoryOk,
      required: false,
      path: "/settings/statutory",
      group: "Compliance",
      description: "PF, ESI, PT, TDS configuration",
      step: 6,
    },
    {
      id: "leave_config",
      label: "Leave Configuration",
      complete: leaveConfigured,
      required: false,
      path: "/settings/leave-setup",
      group: "Leave",
      description: "Leave types, policies, and balance allocation",
      step: 7,
    },
  ];

  const items = uniqueById(rawItems).map((item, idx) => ({
    ...item,
    step: item.step || idx + 1,
  }));

  // Highlight the next incomplete step for sequential guidance.
  out.currentStepId = items.find((i) => !i.complete)?.id || null;
  out.items = items.map((item) => ({
    ...item,
    statusLabel: stepStatusLabel(item.complete, item.id === out.currentStepId),
  }));

  // Status keys used by Settings hub cards (kept even when folded into org step).
  out.statusMap = {
    org_profile: orgStepComplete,
    org_structure: orgStructureOk,
    departments: deptOk,
    designations: desigOk,
    locations: locOk,
    employees: employeesOk,
    payroll_settings: payrollSettingsDone,
    attendance_config: attComplete,
    salary_components: salaryComponentsOk,
    statutory: statutoryOk,
    leave_config: leaveConfigured,
  };

  const tracked = out.items.filter((i) => i.required || i.id === "statutory" || i.id === "leave_config");
  const done = tracked.filter((i) => i.complete).length;
  out.percent = tracked.length ? Math.round((done / tracked.length) * 100) : 0;

  const payrollBlockers = items.filter(
    (i) =>
      ["org_profile", "employees", "payroll_settings", "salary_components", "attendance_config"].includes(i.id) &&
      !i.complete
  );

  out.payrollReady = payrollBlockers.filter(Boolean).length === 0 && payrollPipelineComplete;
  out.payrollReadiness = payrollReadiness;

  if (!orgStepComplete && out.orgProfileComplete && !orgStructureOk) {
    out.alerts.push({
      id: "org_structure_incomplete",
      level: "warn",
      message: `Add ${missingOrgBits.filter((b) => b !== "company profile").join(", ")} under Organization Structure.`,
      path: orgStepPath,
    });
  }
  if (!employeesOk) {
    out.alerts.push({
      id: "no_employees",
      level: "warn",
      message: "No employees have been added yet.",
      path: "/employees/add",
    });
  }
  if (!attComplete) {
    out.alerts.push({
      id: "attendance_not_configured",
      level: "warn",
      message: "Attendance method is not configured.",
      path: "/attendance/configuration",
    });
  }
  /* Holiday calendar is optional for now — not counted in setup % or alerts */
  if (!payrollSettingsDone) {
    out.alerts.push({
      id: "payroll_settings",
      level: "warn",
      message: "Payroll settings are incomplete. Payroll processing will not be available.",
      path: "/salary-v2/setup",
    });
  }
  if (!salaryComponentsOk) {
    out.alerts.push({
      id: "salary_components",
      level: "warn",
      message: "Salary components are not configured.",
      path: "/salary-v2/components",
    });
  }

  return out;
}

export function getPayrollReadinessItems(progress) {
  if (!progress?.items) return [];
  const ids = [
    "org_profile",
    "employees",
    "payroll_settings",
    "attendance_config",
    "salary_components",
    "statutory",
  ];
  return progress.items.filter((i) => ids.includes(i.id));
}

export function canProcessPayroll(progress) {
  if (!progress) return false;
  const required = [
    "org_profile",
    "employees",
    "payroll_settings",
    "attendance_config",
    "salary_components",
  ];
  const blockers = progress.items?.filter((i) => required.includes(i.id) && !i.complete) || [];
  return blockers.length === 0;
}

export function canAddEmployee(progress) {
  if (!progress) return true;
  const org = progress.items?.find((i) => i.id === "org_profile");
  return org ? org.complete : true;
}
